import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<any>;
  children: React.ReactNode;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children }) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const isMouseDown = useRef<boolean>(false);
  const isPullToRefreshMode = useRef<boolean>(false);
  
  const THRESHOLD = 80; // Distance in pixels to trigger refresh
  const ACTIVATION_THRESHOLD = 10; // Minimum distance to consider as a pull-to-refresh gesture
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Check if the target is a form element that should not trigger pull-to-refresh
    const shouldIgnoreTarget = (target: EventTarget | null): boolean => {
      if (!target) return false;
      
      const element = target as HTMLElement;
      const tagName = element.tagName.toLowerCase();
      
      // Ignore form elements and their children
      const formElements = ['input', 'select', 'textarea', 'button', 'a', 'label'];
      if (formElements.includes(tagName)) return true;
      
      // Check for contentEditable elements
      if (element.isContentEditable) return true;
      
      // Check for elements with specific roles
      const role = element.getAttribute('role');
      if (role && ['button', 'checkbox', 'radio', 'slider', 'switch', 'textbox'].includes(role)) return true;
      
      // Check for parent form elements
      let parent = element.parentElement;
      while (parent) {
        if (formElements.includes(parent.tagName.toLowerCase())) return true;
        parent = parent.parentElement;
      }
      
      return false;
    };
    
    // Touch event handlers
    const handleTouchStart = (e: TouchEvent) => {
      // Don't activate pull-to-refresh on form elements
      if (shouldIgnoreTarget(e.target)) {
        startYRef.current = null;
        return;
      }
      
      // Only enable pull to refresh when at the top of the page
      if (window.scrollY <= 0) {
        startYRef.current = e.touches[0].clientY;
        isPullToRefreshMode.current = false;
      } else {
        startYRef.current = null;
      }
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (startYRef.current === null) return;
      
      // Don't interfere with form interactions
      if (shouldIgnoreTarget(e.target)) {
        startYRef.current = null;
        setPullDistance(0);
        setIsPulling(false);
        return;
      }
      
      const currentY = e.touches[0].clientY;
      const diff = currentY - startYRef.current;
      
      // Only allow pulling down, not up
      if (diff > 0) {
        // Apply resistance to make the pull feel more natural
        const distance = Math.min(Math.pow(diff, 0.75), THRESHOLD * 1.5);
        
        // Only activate pull-to-refresh mode if we've moved enough
        if (distance > ACTIVATION_THRESHOLD) {
          isPullToRefreshMode.current = true;
        }
        
        if (isPullToRefreshMode.current) {
          setPullDistance(distance);
          setIsPulling(true);
          
          // Prevent default scrolling behavior when pulling
          if (distance > 5) {
            e.preventDefault();
          }
        }
      }
    };
    
    const handleTouchEnd = () => {
      if (!isPulling || !isPullToRefreshMode.current) {
        // Reset state
        startYRef.current = null;
        isPullToRefreshMode.current = false;
        return;
      }
      
      if (pullDistance >= THRESHOLD) {
        // Trigger refresh
        setIsRefreshing(true);
        setPullDistance(THRESHOLD); // Keep indicator visible during refresh
        
        onRefresh()
          .finally(() => {
            // Reset after refresh completes
            setIsRefreshing(false);
            setPullDistance(0);
            setIsPulling(false);
            isPullToRefreshMode.current = false;
          });
      } else {
        // Reset without refreshing
        setPullDistance(0);
        setIsPulling(false);
        isPullToRefreshMode.current = false;
      }
      
      startYRef.current = null;
    };
    
    // Mouse event handlers (for development on desktop)
    const handleMouseDown = (e: MouseEvent) => {
      // Don't activate pull-to-refresh on form elements
      if (shouldIgnoreTarget(e.target)) {
        startYRef.current = null;
        return;
      }
      
      // Only enable pull to refresh when at the top of the page
      if (window.scrollY <= 0) {
        startYRef.current = e.clientY;
        isMouseDown.current = true;
        isPullToRefreshMode.current = false;
      } else {
        startYRef.current = null;
      }
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (startYRef.current === null || !isMouseDown.current) return;
      
      // Don't interfere with form interactions
      if (shouldIgnoreTarget(e.target)) {
        return;
      }
      
      const currentY = e.clientY;
      const diff = currentY - startYRef.current;
      
      // Only allow pulling down, not up
      if (diff > 0) {
        // Apply resistance to make the pull feel more natural
        const distance = Math.min(Math.pow(diff, 0.75), THRESHOLD * 1.5);
        
        // Only activate pull-to-refresh mode if we've moved enough
        if (distance > ACTIVATION_THRESHOLD) {
          isPullToRefreshMode.current = true;
        }
        
        if (isPullToRefreshMode.current) {
          setPullDistance(distance);
          setIsPulling(true);
        }
      }
    };
    
    const handleMouseUp = () => {
      if (!isMouseDown.current) return;
      
      isMouseDown.current = false;
      
      if (isPulling && isPullToRefreshMode.current && pullDistance >= THRESHOLD) {
        // Trigger refresh
        setIsRefreshing(true);
        setPullDistance(THRESHOLD); // Keep indicator visible during refresh
        
        onRefresh()
          .finally(() => {
            // Reset after refresh completes
            setIsRefreshing(false);
            setPullDistance(0);
            setIsPulling(false);
            isPullToRefreshMode.current = false;
          });
      } else {
        // Reset without refreshing
        setPullDistance(0);
        setIsPulling(false);
        isPullToRefreshMode.current = false;
      }
      
      startYRef.current = null;
    };
    
    // Add event listeners for touch events
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    
    // Add event listeners for mouse events (for development)
    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      // Remove touch event listeners
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      
      // Remove mouse event listeners
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPulling, pullDistance, onRefresh]);
  
  // Calculate progress percentage for visual indicator
  const progress = Math.min(pullDistance / THRESHOLD, 1);
  
  return (
    <div ref={containerRef} className="relative">
      {/* Pull indicator */}
      <div 
        className="absolute left-0 right-0 flex justify-center items-center overflow-hidden z-10 transition-all duration-200"
        style={{ 
          height: `${pullDistance}px`,
          top: 0,
          opacity: pullDistance > 0 ? 1 : 0
        }}
      >
        <div className="flex flex-col items-center justify-center">
          {isRefreshing ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
          ) : (
            <ArrowLeft 
              className="text-primary-500 transition-transform duration-200"
              style={{ 
                transform: `rotate(${270 * progress}deg)`,
                opacity: progress
              }}
              size={24}
            />
          )}
          <div className="text-xs text-on-surface-secondary mt-1">
            {isRefreshing ? 'Uppdaterar...' : progress >= 1 ? 'Släpp för att uppdatera' : 'Dra ner för att uppdatera'}
          </div>
        </div>
      </div>
      
      {/* Main content - modified to not affect fixed positioning */}
      <div 
        style={{ 
          transform: isPulling ? `translateY(${pullDistance}px)` : 'none',
          transition: isPulling ? 'none' : 'transform 0.4s cubic-bezier(0.32, 0.72, 0, 1)',
          // Important: Don't create a new stacking context that would affect fixed positioning
          transformStyle: 'flat',
          isolation: 'auto',
          position: 'relative',
          zIndex: 1
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh; 